import { useEffect, useState } from 'react';

/**
 * Mirrors `active`, but only turns TRUE once `active` has stayed true for
 * `delayMs` without interruption. Turning false is immediate.
 *
 * This exists for loading indicators. A refetch that resolves in 150 ms and
 * flashes a spinner reads as a glitch, not as feedback — worse than showing
 * nothing. Gating on a short delay means the indicator appears only when the
 * wait is long enough that the user has started to wonder.
 *
 * Deliberately asymmetric: the hide is instant. Holding an indicator on screen
 * after the data arrived would be showing the user something untrue.
 */
export function useDelayedFlag(active: boolean, delayMs = 250): boolean {
  const [on, setOn] = useState(false);

  useEffect(() => {
    if (!active) {
      setOn(false);
      return;
    }
    const id = setTimeout(() => setOn(true), delayMs);
    return () => clearTimeout(id);
  }, [active, delayMs]);

  return on;
}
